import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import convertDataAttributes from '@widgets/shared/convertDataAttributes';
import { Suspense, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, } from 'preact/compat';
import { DirectDebitFormCTX, optionsDefaults } from '@widgets/DirectDebitForm/context/Options';
import LoadingSpinner from '@widgets/shared/components/LoadingSpinner';
import { FormProvider, useForm } from 'react-hook-form';
import { directDebitSchema } from '@widgets/DirectDebitForm/validation/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import DOMPurify from 'dompurify';
import TextInput from '@widgets/shared/components/form-controls/TextInput';
import SortCodeInput from '@widgets/shared/components/form-controls/SortCodeInput';
import ToggleInput from '@widgets/shared/components/form-controls/ToggleInput';
import API from '@widgets/shared/API/connection';
import { successUrlRedirect } from '@widgets/shared/registerOrLoginAction';
import FormNotices from '@widgets/shared/components/FormNotices';
import { Title, Wrapper, Copy } from './styles';
function Form() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [hasServerErrors, setHasServerErrors] = useState(false);
    const handleErrorClose = useCallback(() => {
        setErrorMessage(undefined);
    }, []);
    const ref = useRef(null);
    const { formTitle, formCopy, submissionUrl, termsContent, termsSubheading, termsHeading, directDebitLogo, termsAndConditionsText, directDebitConditionsText, overviewTitle, initialPrice, initialPriceCopy, monthlyPrice, monthlyPriceCopy, overrideApiRoute, } = useContext(DirectDebitFormCTX);
    const formKey = useId();
    const form = useForm({
        mode: 'onChange',
        resolver: yupResolver(directDebitSchema),
    });
    const { watch, getValues, formState: { errors, isValid }, setError, clearErrors, subscribe, } = form;
    const inputChanged = useCallback(() => {
        if (hasServerErrors) {
            clearErrors();
        }
    }, [hasServerErrors, clearErrors]);
    useEffect(() => {
        subscribe({
            formState: {
                values: true,
            },
            callback: ({ values }) => {
                inputChanged();
            },
        });
    }, [inputChanged, subscribe]);
    const handleAnyInputChanged = useCallback(() => {
        console.log('Changing');
        if (hasServerErrors) {
            clearErrors();
            setHasServerErrors(false);
        }
    }, [clearErrors, hasServerErrors]);
    // useEffect(() => {
    //   handleAnyInputChanged();
    // }, [formValues]);
    const handleSubmit = useCallback((e) => {
        if (!overrideApiRoute) {
            e.preventDefault();
            setIsSubmitting(true);
            API.directDebit(getValues()).then(({ successUrl: remoteSubmissionUrl, success, errors: fieldErrors, message, }) => {
                if (success) {
                    if (remoteSubmissionUrl || submissionUrl) {
                        successUrlRedirect(remoteSubmissionUrl, submissionUrl);
                    }
                }
                else {
                    setHasServerErrors(true);
                    fieldErrors.forEach(({ message: errorMessage, field: errorField }) => {
                        if (errorField) {
                            setError(errorField, { type: 'custom', message: errorMessage }, { shouldFocus: false });
                        }
                    });
                    if (message) {
                        setErrorMessage(message);
                    }
                }
            })
                .finally(() => {
                setIsSubmitting(false);
            });
        }
    }, [overrideApiRoute, getValues, submissionUrl, setError]);
    return (_jsx(FormProvider, { ...form, children: _jsx(Wrapper, { action: submissionUrl, method: "POST", ref: ref, noValidate: true, onSubmit: handleSubmit, className: "form", children: _jsxs("div", { className: "rfx-grid padding-large", children: [_jsx("div", { className: "rfx-col-md-7", children: _jsxs("div", { className: "slab", children: [_jsxs("div", { className: "terms-and-conditions terms-and-conditions--direct-debit", children: [_jsxs("div", { className: "terms-and-conditions__header", children: [_jsxs("div", { className: "terms-and-conditions__title-wrapper", children: [_jsx("span", { className: "terms-and-conditions__title h3", children: termsHeading }), _jsx("picture", { className: "terms-and-conditions__logo", children: _jsx("img", { src: directDebitLogo, alt: "Direct debit logo" }) })] }), _jsx("span", { className: "terms-and-conditions__copy p", children: termsSubheading })] }), _jsx("div", { className: "terms-and-conditions__terms terms", dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(termsContent) } })] }), _jsx(Title, { children: formTitle }), _jsx(Copy, { children: formCopy }), errorMessage && (_jsx("div", { style: { margin: '1rem 0' }, children: _jsx(FormNotices, { message: errorMessage, type: "error", handleClose: handleErrorClose }) })), _jsx(TextInput, { inputAttrs: {
                                        id: 'account-name',
                                        name: 'accountName',
                                        type: 'text',
                                    }, labelAttrs: {
                                        htmlFor: 'account-name',
                                        text: 'Account Holder\'s Name',
                                        isFieldRequired: true,
                                    }, maybeTextTransform: (accountName) => accountName.toUpperCase() }), _jsx(TextInput, { inputAttrs: {
                                        id: 'account-number',
                                        name: 'accountNumber',
                                        type: 'number',
                                        minlength: 8,
                                        maxLength: 8,
                                    }, labelAttrs: {
                                        htmlFor: 'account-number',
                                        text: 'Account Number',
                                        isFieldRequired: true,
                                    } }), _jsx(SortCodeInput, { inputAttrs: {
                                        id: 'sort-code',
                                        name: 'sortCode',
                                        type: 'number',
                                        minlength: 2,
                                        maxLength: 2,
                                    }, labelAttrs: {
                                        htmlFor: 'account-number',
                                        text: 'Sort Code',
                                        isFieldRequired: true,
                                    } }), _jsx("input", { type: "hidden", name: "sortCode", value: watch('sortCode') }), _jsx(ToggleInput, { inputAttrs: {
                                        id: 'terms',
                                        name: 'termsAccepted',
                                    }, labelAttrs: {
                                        htmlFor: 'terms',
                                        text: termsAndConditionsText,
                                        isFieldRequired: true,
                                    } }), _jsx(ToggleInput, { inputAttrs: {
                                        id: 'direct-debit-terms',
                                        name: 'directDebitAccepted',
                                    }, labelAttrs: {
                                        htmlFor: 'direct-debit-terms',
                                        text: directDebitConditionsText,
                                        isFieldRequired: true,
                                    } })] }) }), _jsx("div", { className: "rfx-col-md-5", children: _jsxs("div", { className: "overview overview--monthly", children: [overviewTitle && (_jsx("span", { className: "overview__title", children: overviewTitle })), _jsxs("div", { className: "overview__row", children: [initialPriceCopy && (_jsx("span", { className: "overview__subtitle", children: initialPriceCopy })), _jsx("span", { className: "overview__total", children: initialPrice })] }), monthlyPrice && (_jsxs("div", { className: "overview__row", children: [monthlyPriceCopy && (_jsx("span", { className: "overview__subtitle", children: monthlyPriceCopy })), _jsx("span", { className: "overview__total overview__total--small", children: monthlyPrice })] })), _jsx("button", { type: "submit", disabled: !isValid || isSubmitting, className: "btn btn--secondary", children: "Continue" })] }) })] }) }, `form-${formKey}`) }));
}
function DirectDebitForm(props) {
    const { dataset } = props;
    const formattedAttributes = convertDataAttributes(dataset ?? {});
    const formOptions = useMemo(() => ({
        ...optionsDefaults,
        ...formattedAttributes,
    }), [formattedAttributes]);
    return (_jsx(Suspense, { fallback: LoadingSpinner, children: _jsx(DirectDebitFormCTX.Provider, { value: formOptions, children: _jsx(Form, {}) }) }));
}
export default DirectDebitForm;
